import { ChevronRight } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { Kpi, Block, Badge, Loading, useLoad, eur, num, fdate, fdatetime } from '@/components/dashboard/kit';
import { PersonCell, TestBadge, TIER, eventTitle, plural } from './people';

/*
  Resumen del panel: lo que hay que mirar hoy. Personas por nivel, tests nuevos, próximas inscripciones y
  pagos pendientes. Las cifras de dinero (summary.money) solo llegan al superadmin.
  Cada bloque lleva a la lista o a la ficha correspondiente.
*/
const rowCls = 'w-full min-h-[48px] flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[0.03] focus:bg-white/[0.04] outline-none';

export default function AdminSummary({ go, openPerson }) {
  const { data, error } = useLoad(() => dashboardApi.adminSummary(), []);
  if (!data) return <Loading error={error} />;
  const { users, tests, registrations } = data;

  return (
    <div className="space-y-8" data-testid="admin-summary">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Usuarios con cuenta" value={num(users.total)} sub={`${num(data.contacts_without_account)} contactos sin cuenta`} />
        <Kpi label="Tests esta semana" value={num(tests.week)} sub={`${num(tests.hot_week)} calientes · ${num(tests.booking_week)} abrieron la agenda`} />
        <Kpi label="Inscripciones próximas" value={num(registrations.upcoming)} sub="eventos y bootcamps por celebrar" />
        <Kpi label="Pagos pendientes" value={num(registrations.pending_payments)} tone={registrations.pending_payments ? 'bad' : undefined}
          sub={registrations.pending_total > registrations.pending_payments ? `${num(registrations.pending_total)} contando los ya celebrados` : 'de próximas inscripciones'} />
      </div>

      <Block title="Usuarios por nivel">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {['free', 'access', 'pro', 'premium', 'admin', 'superadmin'].map((t) => (
            <button key={t} type="button" onClick={() => go('personas', { sub: 'usuarios', nivel: t })}
              className="min-h-[56px] rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-left hover:border-gold/30">
              <span className="block text-[11px] uppercase tracking-wider text-gray-400">{TIER[t][0]}</span>
              <span className="block text-lg font-semibold text-white tabular-nums">{num(users[t])}</span>
            </button>
          ))}
        </div>
      </Block>

      {data.money && (
        <Block title="Dirección" right={<button type="button" onClick={() => go('direccion')} className="text-sm text-gold hover:underline min-h-[40px]">Abrir el cuadro de mando</button>}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Volumen de ventas" value={eur(data.money.sales_volume)} />
            <Kpi label="Aportaciones" value={eur(data.money.contributions)} />
            <Kpi label="Comisiones devengadas" value={eur(data.money.commissions_accrued)} sub={`${eur(data.money.commissions_paid)} pagadas`} />
            <Kpi label="Comisiones exigibles" value={eur(data.money.commissions_payable)} />
          </div>
        </Block>
      )}

      <div className="grid gap-8 lg:grid-cols-2">
        <Block title="Próximos eventos y bootcamps" right={<button type="button" onClick={() => go('eventos')} className="text-sm text-gold hover:underline min-h-[40px]">Gestionar</button>}>
          {!data.upcoming_events.length ? (
            <p className="rounded-xl border border-dashed border-white/[0.08] py-8 text-center text-sm text-gray-400">No hay eventos ni bootcamps próximos.</p>
          ) : (
            <ul className="rounded-xl border border-white/[0.06] divide-y divide-white/[0.05]">
              {data.upcoming_events.map((e) => (
                <li key={e.key}>
                  <button type="button" className={rowCls} onClick={() => go('personas', { sub: 'inscripciones', event: e.key })}>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-white truncate">{eventTitle(e.kind, e.title)}</span>
                      <span className="block text-xs text-gray-400">{fdate(e.date)} · {plural(e.count, 'inscrito', 'inscritos')}</span>
                    </span>
                    {e.pending > 0 && <Badge tone="warn">{plural(e.pending, 'pago pendiente', 'pagos pendientes')}</Badge>}
                    <ChevronRight size={16} className="text-gray-500 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Block>

        <Block title="Últimos tests de idoneidad" right={<button type="button" onClick={() => go('personas', { sub: 'tests' })} className="text-sm text-gold hover:underline min-h-[40px]">Ver todos</button>}>
          {!data.latest_tests.length ? (
            <p className="rounded-xl border border-dashed border-white/[0.08] py-8 text-center text-sm text-gray-400">Todavía nadie ha hecho el test.</p>
          ) : (
            <ul className="rounded-xl border border-white/[0.06] divide-y divide-white/[0.05]">
              {data.latest_tests.map((t, i) => (
                <li key={`${t.key}-${i}`}>
                  <button type="button" className={rowCls} onClick={() => openPerson(t.key)}>
                    <span className="min-w-0 flex-1"><PersonCell name={t.name} email={fdatetime(t.created_at)} /></span>
                    {!t.has_account && <span className="hidden sm:inline"><Badge>Sin cuenta</Badge></span>}
                    <TestBadge temperature={t.temperature} score={t.score} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Block>
      </div>
    </div>
  );
}
